import { createCrudApi, toQuery } from 'src/shared/api/crudApi';
import { httpClient } from 'src/shared/api/httpClient';
import { API_PREFIX } from 'src/shared/constants';
import type {
  Appointment,
  AppointmentEvent,
  AppointmentListParams,
  AppointmentPayload,
  AppointmentUpdatePayload,
  Availability,
  AvailabilityParams,
  CalendarParams,
  ReschedulePayload,
  StatusPayload,
} from './types';

const base = `${API_PREFIX}/appointments`;

const crud = createCrudApi<
  Appointment,
  AppointmentListParams,
  AppointmentPayload,
  AppointmentUpdatePayload,
  number
>(base);

export const appointmentsApi = {
  list: crud.list,
  get: crud.get,
  create: crud.create,
  update: crud.update,
  // `remove` is deliberately not re-exported: the API has no such route. An
  // appointment is cancelled through `changeStatus`, because payments, photos
  // and reminders all point at its id.

  /**
   * Every appointment in a range of local days, unpaginated.
   *
   * A grid cannot render a page of its own contents — handed page 1 of 3 it
   * would draw a Tuesday with two of its five appointments and no way to know
   * the rest exist. The API caps the range at 62 days instead.
   */
  calendar: (params: CalendarParams) =>
    httpClient.get<Appointment[]>(`${base}/calendar?${toQuery(params)}`),

  /** Free start times on one local day, already in the business's timezone. */
  availability: (params: AvailabilityParams) =>
    httpClient.get<Availability>(`${base}/availability?${toQuery(params)}`),

  events: (id: number) => httpClient.get<AppointmentEvent[]>(`${base}/${id}/events`),

  reschedule: (id: number, data: ReschedulePayload) =>
    httpClient.patch<Appointment>(`${base}/${id}/reschedule`, data),

  changeStatus: (id: number, data: StatusPayload) =>
    httpClient.patch<Appointment>(`${base}/${id}/status`, data),
};
